import React, { useEffect, useRef, useState } from 'react';
import { HorizonInterval } from 'src/models/Horizon';
import ReportHorizonSequencing from 'src/models/ReportHorizonSequencing';
import ReportHorizonSequencingXRef from 'src/models/ReportHorizonSequencingXRef';
import useFetch from 'use-http';
import HorizonSequencingComponent from '../TractComponents/AddHorizonComponents/HorizonSequencingComponent';
import Button from 'src/components/controls/forms/redesign/FormButton';
import CollapsibleSection from 'src/components/common/CollapsibleSection';

// ── Self-contained move-to input ─────────────────────────────────────────
// Each card gets its own instance so they don't share position state.
interface IMoveToInputProps {
    sequenceLength: number;
    onMove: (targetPos: number) => void;
}

const MoveToInput = ({ sequenceLength, onMove }: IMoveToInputProps) => {
    const [value, setValue] = useState<string>('');

    const targetPos = value === '' ? undefined : parseInt(value, 10) - 1;
    const isValid = targetPos !== undefined && targetPos >= 0 && targetPos < sequenceLength;

    const handleGo = () => {
        if (!isValid) { return; }
        onMove(targetPos);
        setValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') { handleGo(); }
    };

    return (
        <div className="horizon-card-moveto">
            <label className="horizon-card-moveto__label">Go to</label>
            <input
                className="horizon-card-moveto__input"
                type="number"
                value={value}
                placeholder="#"
                min={1}
                max={sequenceLength}
                onChange={(e) => setValue(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
            />
            <Button
                className="blue-hover-button"
                style={{ padding: '0 .6rem', height: '2.2rem', fontSize: '.8rem' }}
                text="Go"
                disabled={!isValid}
                onClick={handleGo}
            />
        </div>
    );
};

interface IReportPreviewDataProps {
    selectedHorizons: HorizonInterval[];
    selectedSequence: ReportHorizonSequencing;
    clientId: number;
    projectId: number;
    updateHorizonSequences: () => void;
}

const ReportPreviewData = (props: IReportPreviewDataProps) => {
    const sequenceWindowRef: React.MutableRefObject<HTMLDivElement> = useRef();

    const { put, post, response } = useFetch();
    const [horizonOrder, setHorizonOrder] = useState<HorizonInterval[]>(props.selectedHorizons);
    const [loadingDone, setLoadingDone] = useState<boolean>(false);
    const [intervalList, setIntervalList] = useState<HorizonInterval[][]>([]);
    const [unsequencedList, setUnsequencedList] = useState<HorizonInterval[]>([]);

    useEffect(() => {
        setLoadingDone(false);

        let refHorizons = [...props.selectedHorizons];

        props.selectedSequence?.horizonSequences?.forEach(hs => {
            let index = refHorizons.indexOf(refHorizons.find(sh => sh.id === hs.horizonIntervalId));
            if (index > -1) {
                let reference = refHorizons[index];
                reference.sequencePosition = hs.sequencePosition;
                reference.from?.calcType();
                reference.to?.calcType();
                refHorizons[index] = reference;
            }
        });

        refHorizons = refHorizons.sort((r1, r2) => { return r1.sequencePosition - r2.sequencePosition; });
        setHorizonOrder(refHorizons);
        // Set this up through parameters to prevent race conditions.
        updateIntervalList(refHorizons, props.selectedSequence);
        setLoadingDone(true);
    }, [props.selectedHorizons, props.selectedSequence]);

    const sequenceHorizons = async (sequence: Array<{ id: number; sequence: number }>) => {
        let sequenceReport: ReportHorizonSequencing = new ReportHorizonSequencing;
        let sequences: ReportHorizonSequencingXRef[] = [];
        sequenceReport.projectId = props.projectId;
        sequenceReport.clientId = props.clientId;

        if (props.selectedSequence !== undefined) {
            sequenceReport = props.selectedSequence;
            sequences = sequenceReport.horizonSequences;

            sequence.forEach(seq => {
                let index = sequences.indexOf(sequences.find(s => s.horizonIntervalId === seq.id));
                if (index > -1) {
                    let ref = sequences[index];
                    ref.sequencePosition = seq.sequence;
                    sequences[index] = ref;
                } else {
                    sequences.push(new ReportHorizonSequencingXRef(seq.id, seq.sequence));
                }
            });

            await put(`v1.0/reports/sequence-horizons`, sequenceReport);
        } else {
            sequence.forEach(s => {
                sequences.push(new ReportHorizonSequencingXRef(s.id, s.sequence));
            });
            sequenceReport.horizonSequences = sequences;

            await post(`v1.0/reports/sequence-horizons`, sequenceReport);
        }

        if (response.ok) {
            props.updateHorizonSequences();
        }
    };

    // ── Card rendered inside each sequenced horizon group ──────────────────
    // currSequence = 0-based index of the group in intervalList
    // currIndex    = index within the group (-1 means "primary" / first item,
    //                but here we pass 0 for first and 1+ for aliases)
    const horizonCard = (
        interval: HorizonInterval,
        currSequence: number,
        sequenceLength: number,
        currIndex = -1
    ) => {
        if (!interval?.to) {
            return <span>Horizon depth undefined</span>;
        }

        const isFirst = currIndex <= 0;          // primary item in the group
        const isGroupMember = currIndex > 0;     // alias inside "Same As" collapse

        return (
            <div className="horizon-card-row">
                {/* Horizon label */}
                <span className="horizon-card-label">
                    {`To ${interval.to.toString()}`}
                </span>

                {/* Action buttons – only on the primary (first) card */}
                {isFirst && (
                    <div className="horizon-card-actions">
                        <button
                            className="horizon-card-btn"
                            title="Move Up"
                            disabled={currSequence === 0}
                            onClick={() => reorderList(currSequence, currSequence - 1, false)}
                        >
                            ▲
                        </button>
                        <button
                            className="horizon-card-btn"
                            title="Move Down"
                            disabled={currSequence >= sequenceLength - 1}
                            onClick={() => reorderList(currSequence, currSequence + 1, false)}
                        >
                            ▼
                        </button>
                        <button
                            className="horizon-card-btn horizon-card-btn--combine"
                            title="Combine with above (same horizon)"
                            disabled={currSequence === 0}
                            onClick={() => reorderList(currSequence, currSequence - 1, true)}
                        >
                            ⊕ Group
                        </button>

                        {/* Compact move-to input — self-contained so each card tracks its own value */}
                        <MoveToInput
                            sequenceLength={sequenceLength}
                            onMove={(targetPos) => reorderList(currSequence, targetPos, false)}
                        />

                        {/* Remove from sequenced list */}
                        <Button
                            className="blue-hover-button horizon-card-btn--remove"
                            style={{ padding: '0 .6rem', height: '2.2rem', fontSize: '.8rem' }}
                            text="✕"
                            title="Remove from sequence"
                            onClick={() => reorderList(currSequence, -1, false)}
                        />
                    </div>
                )}

                {/* Remove alias from group */}
                {isGroupMember && (
                    <div className="horizon-card-actions horizon-card-actions--alias">
                        <Button
                            className="blue-hover-button"
                            style={{ padding: '0 .6rem', height: '2rem', fontSize: '.75rem' }}
                            text="✕ Ungroup"
                            title="Remove from group"
                            onClick={() => reorderList(currSequence, -1, false, currIndex)}
                        />
                    </div>
                )}
            </div>
        );
    };

    // ── Full group card (primary + "Same As" aliases) ──────────────────────
    const customHorizonSequenceFormatting = (
        intervals: HorizonInterval[],
        currSequence: number,
        sequenceLength: number
    ) => {
        const iFirst = intervals[0];

        if (!iFirst?.to) {
            return <span>Horizon depth undefined</span>;
        }

        return (
            <div className="horizon-group-card">
                {/* Sequence badge */}
                <span className="horizon-group-card__badge">#{currSequence + 1}</span>

                {/* Primary card */}
                {horizonCard(iFirst, currSequence, sequenceLength, 0)}

                {/* Aliases */}
                {intervals.length > 1 && (
                    <div className="horizon-group-card__aliases" key={`sequence-position-${currSequence}`}>
                        <CollapsibleSection title={`Same As (${intervals.length - 1})`} startCollapsed={true}>
                            {intervals.slice(1).map((hi: HorizonInterval, index: number) => (
                                // index + 1 because we sliced off the first
                                <div key={`alias-${hi.id}`} className="horizon-group-card__alias-row">
                                    {horizonCard(hi, currSequence, sequenceLength, index + 1)}
                                </div>
                            ))}
                        </CollapsibleSection>
                    </div>
                )}
            </div>
        );
    };

    const reorderList = (sourcePosition: number, destPosition: number, combine: boolean, sourceIndex: number = -1) => {
        // Deep copy to avoid mutating the existing state arrays in place
        let result: HorizonInterval[][] = intervalList.map(group => [...group]);
        let unsequenced: HorizonInterval[] = [...unsequencedList];

        // ── Step 1: Extract the item(s) being moved ───────────────────────
        // removed is always HorizonInterval[] for consistency — a group or a one-item array
        let removed: HorizonInterval[];

        if (sourcePosition > -1) {
            if (sourceIndex > -1) {
                // Single alias pulled out of a group sub-array
                const item = result[sourcePosition][sourceIndex];
                result[sourcePosition] = result[sourcePosition].filter((_, i) => i !== sourceIndex);
                removed = [item];
            } else {
                // Entire group row removed from the sequenced list
                removed = result[sourcePosition];
                result = result.filter((_, i) => i !== sourcePosition);
                // All indices above sourcePosition have now shifted down by 1
            }
        } else {
            // Single item from the unsequenced list
            const item = unsequenced[sourceIndex];
            unsequenced = unsequenced.filter((_, i) => i !== sourceIndex);
            removed = [item];
        }

        // ── Step 2: Place at destination ──────────────────────────────────
        if (destPosition === -1) {
            // Return to unsequenced
            unsequenced = unsequenced.concat(removed);

        } else if (combine) {
            // Merge into an existing slot.
            // If we removed a full group (sourceIndex === -1), indices above sourcePosition
            // already shifted down, so adjust the merge target accordingly.
            let mergeTarget = destPosition;
            if (sourceIndex === -1 && destPosition > sourcePosition) {
                mergeTarget = destPosition - 1;
            }
            if (result[mergeTarget] !== undefined) {
                result[mergeTarget] = result[mergeTarget].concat(removed);
            } else {
                // Fallback: target slot missing, push as new group
                result.push(removed);
            }

        } else {
            // Reorder: insert as a new slot at destPosition.
            // After removing a full group (sourceIndex === -1), the array has already
            // shifted, so destPosition from the button (currSequence ± 1) needs no
            // further adjustment — it refers to the post-removal array.
            const clampedDest = Math.max(0, Math.min(destPosition, result.length));
            result.splice(clampedDest, 0, removed);
        }

        // ── Step 3: Clean up empty group slots ────────────────────────────
        result = result.filter(group => group && group.length > 0);

        // ── Step 4: Re-assign sequence positions ──────────────────────────
        result = result.map((group, groupIndex) =>
            group.map(hi => {
                hi.sequencePosition = groupIndex + 1;
                return hi;
            })
        );
        unsequenced = unsequenced.map(hi => {
            hi.sequencePosition = -1;
            return hi;
        });

        setIntervalList(result);
        setUnsequencedList(unsequenced);
    };

    const updateIntervalList = (refHorizons: HorizonInterval[], selectedSequence: ReportHorizonSequencing) => {
        let horizons = [...refHorizons];
        let newInterval = [];

        if (selectedSequence !== undefined && selectedSequence.horizonSequences?.length > 0) {
            horizons = horizons.sort((a, b) => {
                if (a.sequencePosition < b.sequencePosition) { return -1; }
                if (a.sequencePosition > b.sequencePosition) { return 1; }
                return 0;
            });

            for (let h of horizons) {
                if (h.sequencePosition > newInterval.length) {
                    newInterval.push([h]);
                } else {
                    newInterval[h.sequencePosition - 1].push(h);
                }
            }
            setUnsequencedList([]);
        } else {
            let depthHorizons = horizons.filter(dh => !!dh.to?.depth);
            let customHorizons = horizons.filter(h => !h.to?.depth && h.to?.isCenterEarth !== true);
            let coeHorizons = horizons.filter(h => h.to?.isCenterEarth === true);

            depthHorizons = depthHorizons.sort((a, b) => a.to.depth - b.to.depth);

            let currDepth = 0;
            for (let h of depthHorizons) {
                if (h.to.depth === currDepth) {
                    newInterval[newInterval.length - 1].push(h);
                } else {
                    newInterval.push([h]);
                    currDepth = h.to.depth;
                }
            }

            if (coeHorizons.length > 0) {
                newInterval.push(coeHorizons);
            }
            setUnsequencedList(customHorizons);
        }

        setIntervalList(newInterval);
    };

    return (
        <div ref={sequenceWindowRef} style={{ paddingBottom: '1rem' }}>
            {loadingDone && (
                <HorizonSequencingComponent
                    horizons={horizonOrder}
                    intervalList={intervalList}
                    unsequencedList={unsequencedList}
                    sequenceHorizons={sequenceHorizons}
                    isInDialog={false}
                    hasMultipleTracts={true}
                    viewportRef={sequenceWindowRef}
                    customListFormat={customHorizonSequenceFormatting}
                    customDetailFormat={horizonCard}
                    reorderList={reorderList}
                />
            )}
        </div>
    );
};

export default ReportPreviewData;



// WEBPACK FOOTER //
// ./src/components/project/sections/ReportComponents/ReportPreviewData.tsx